import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api, examApi } from '../services/api';
import '../styles/RankingPage.css';

interface RankingEntry {
  examType: string;
  month: string;
  userId: string;
  userName: string;
  userEmail: string;
  score: number;
  completedAt: string;
  rank: number;
}

interface ExamType {
  exam_type_id: string;
  name: string;
  description: string;
  icon?: string;
}

interface RankingListResponse {
  rankings: RankingEntry[];
  hasMore: boolean;
}

interface UserRankResponse {
  rank: number;
  totalUsers: number;
  entry?: RankingEntry;
}

const RankingPage: React.FC = () => {
  const { user } = useAuth();
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [selectedExamType, setSelectedExamType] = useState<string>('');
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [rankings, setRankings] = useState<RankingEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'monthly' | 'all-time'>('monthly');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [userRank, setUserRank] = useState<UserRankResponse | null>(null);

  const fetchUserRank = useCallback(async () => {
    if (!user) return;
    try {
      const params = viewMode === 'monthly' 
        ? `?month=${selectedMonth}&viewMode=monthly`
        : '?viewMode=all-time';
      const response = await api.get<UserRankResponse>(`/ranking/${selectedExamType}/user/${user.user_id}${params}`);
      if (response.success && response.data) {
        setUserRank(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user rank:', error);
    }
  }, [selectedExamType, selectedMonth, user, viewMode]);

  const fetchMonthlyRanking = useCallback(async (targetOffset = offset) => {
    setLoading(true);
    try {
      const response = await api.get<RankingListResponse>(`/ranking/${selectedExamType}/monthly/${selectedMonth}?limit=50&offset=${targetOffset}`);
      if (response.success && response.data) {
        const data = response.data;
        if (targetOffset === 0) {
          setRankings(data.rankings);
        } else {
          setRankings(prev => [...prev, ...data.rankings]);
        }
        setHasMore(data.hasMore);
      }
    } catch (error) {
      console.error('Failed to fetch monthly ranking:', error);
    } finally {
      setLoading(false);
    }
  }, [offset, selectedExamType, selectedMonth]);

  const fetchAllTimeRanking = useCallback(async (targetOffset = offset) => {
    setLoading(true);
    try {
      const response = await api.get<RankingListResponse>(`/ranking/${selectedExamType}/all-time?limit=50&offset=${targetOffset}`);
      if (response.success && response.data) {
        const data = response.data;
        if (targetOffset === 0) {
          setRankings(data.rankings);
        } else {
          setRankings(prev => [...prev, ...data.rankings]);
        }
        setHasMore(data.hasMore);
      }
    } catch (error) {
      console.error('Failed to fetch all-time ranking:', error);
    } finally {
      setLoading(false);
    }
  }, [offset, selectedExamType]);

  const fetchAvailableMonths = useCallback(async () => {
    try {
      const response = await api.get<string[]>(`/ranking/${selectedExamType}/months`);
      if (response.success && response.data) {
        setAvailableMonths(response.data);
        if (response.data.length > 0) {
          setSelectedMonth(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch available months:', error);
    }
  }, [selectedExamType]);

  const fetchExamTypes = useCallback(async () => {
    try {
      const response = await examApi.getTypes(); // 접근 제한이 적용된 시험 유형만 가져오기
      if (response.success && response.data) {
        setExamTypes(response.data as ExamType[]);
        if ((response.data as ExamType[]).length > 0) {
          setSelectedExamType((response.data as ExamType[])[0].exam_type_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch exam types:', error);
    }
  }, []);

  useEffect(() => {
    fetchExamTypes();
  }, [fetchExamTypes]);

  useEffect(() => {
    if (selectedExamType) {
      setOffset(0);
      if (viewMode === 'monthly') {
        fetchAvailableMonths();
      } else {
        fetchAllTimeRanking(0);
      }
    }
  }, [fetchAllTimeRanking, fetchAvailableMonths, selectedExamType, viewMode]);

  useEffect(() => {
    if (selectedExamType && selectedMonth && viewMode === 'monthly') {
      setOffset(0);
      fetchMonthlyRanking(0);
    }
  }, [fetchMonthlyRanking, selectedExamType, selectedMonth, viewMode]);

  useEffect(() => {
    if (selectedExamType && user) {
      fetchUserRank();
    }
  }, [fetchUserRank, selectedExamType, user]);

  const loadMore = () => {
    const newOffset = offset + 50;
    setOffset(newOffset);
    if (viewMode === 'monthly') {
      fetchMonthlyRanking(newOffset);
    } else {
      fetchAllTimeRanking(newOffset);
    }
  };

  const downloadCSV = async () => {
    if (!selectedExamType) return;
    
    try {
      const params = viewMode === 'monthly' && selectedMonth
        ? `?viewMode=monthly&month=${selectedMonth}`
        : '?viewMode=all-time';
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/ranking/${selectedExamType}/export/csv${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const filename = viewMode === 'monthly' && selectedMonth 
          ? `${selectedExamType}-ranking-${selectedMonth}.csv`
          : `${selectedExamType}-ranking-all-time.csv`;
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('CSV download failed:', response.status);
      }
    } catch (error) {
      console.error('Failed to download CSV:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatMonth = (monthString: string) => {
    const [year, month] = monthString.split('-');
    return `${year}년 ${parseInt(month)}월`;
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `${rank}위`;
    }
  };

  const isCurrentUser = (entry: RankingEntry) => {
    return user && entry.userEmail === user.email;
  };

  const formatScore = (score?: number) => {
    if (typeof score !== 'number') return '-';
    return `${score}%`;
  };

  const topThree = rankings.filter((entry) => entry.rank <= 3).sort((a, b) => a.rank - b.rank);
  const tableRankings = rankings.filter((entry) => entry.rank >= 4);

  return (
    <div className="ranking-page">
      <div className="ranking-header">
        <h1>🏆 랭킹</h1>
        <p>시험 유형별 최고 점수 랭킹을 확인하세요</p>
      </div>

      <div className="ranking-controls">
        <div className="control-group">
          <label>시험 유형</label>
          <select 
            value={selectedExamType} 
            onChange={(e) => setSelectedExamType(e.target.value)}
          >
            {examTypes.map(type => (
              <option key={type.exam_type_id} value={type.exam_type_id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>기간</label>
          <div className="view-mode-toggle">
            <button 
              className={viewMode === 'monthly' ? 'active' : ''}
              onClick={() => setViewMode('monthly')}
            >
              월별
            </button>
            <button 
              className={viewMode === 'all-time' ? 'active' : ''}
              onClick={() => setViewMode('all-time')}
            >
              전체
            </button>
          </div>
        </div>

        {viewMode === 'monthly' && (
          <div className="control-group">
            <label>월</label>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {formatMonth(month)}
                </option>
              ))}
            </select>
          </div>
        )}

        {user?.is_admin && (
          <div className="control-group">
            <label>&nbsp;</label>
            <button onClick={downloadCSV} className="export-btn">
              📄 CSV 다운로드
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>랭킹을 불러오는 중...</p>
        </div>
      ) : (
        <div className="ranking-table-section">
          {rankings.length === 0 ? (
            <div className="no-rankings">
              <p>아직 랭킹 데이터가 없습니다.</p>
            </div>
          ) : (
            <>
              {topThree.length > 0 && (
                <div className="podium-wrap">
                  <div className="podium-grid">
                    {[2, 1, 3].map((rank) => {
                      const entry = topThree.find((item) => item.rank === rank);
                      if (!entry) {
                        return <div key={`empty-${rank}`} className="podium-card podium-card-empty" />;
                      }
                      return (
                        <div
                          key={`${entry.userId}-${entry.completedAt}`}
                          className={`podium-card rank-${rank} ${isCurrentUser(entry) ? 'is-current-user' : ''}`}
                        >
                          <div className="podium-rank">{getRankIcon(rank)}</div>
                          <div className="podium-user">
                            {entry.userName}
                            {isCurrentUser(entry) && <span className="you-badge">나</span>}
                          </div>
                          <div className="podium-score">{formatScore(entry.score)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="ranking-table-wrap">
                <table className="ranking-table">
                  <thead>
                    <tr>
                      <th className="col-rank">순위</th>
                      <th className="col-user">사용자</th>
                      <th className="col-score">점수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRankings.map((entry) => (
                      <tr
                        key={`${entry.userId}-${entry.completedAt}`}
                        className={isCurrentUser(entry) ? 'is-current-user' : ''}
                      >
                        <td className="rank-cell">{entry.rank}위</td>
                        <td className="user-cell">
                          <span className="user-name">{entry.userName}</span>
                          {isCurrentUser(entry) && <span className="you-badge">나</span>}
                        </td>
                        <td className="score-cell">{formatScore(entry.score)}</td>
                      </tr>
                    ))}
                    {tableRankings.length === 0 && (
                      <tr>
                        <td colSpan={3} className="table-empty">
                          Top 3만 집계되었습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          
          {hasMore && (
            <div className="load-more">
              <button onClick={loadMore} className="load-more-btn">
                더 보기
              </button>
            </div>
          )}
          

        </div>
      )}

      {userRank && !rankings.some(r => r.userId === user?.user_id) && (
        <div className="my-rank-section">
          <h3>내 랭킹</h3>
          <div className="my-rank-card">
            <span className="my-rank-label">현재 순위</span>
            <strong>{userRank.rank}위</strong>
            <span className="my-rank-score">{formatScore(userRank.entry?.score)}</span>
          </div>
          <p className="rank-summary">
            전체 {userRank.totalUsers}명 중 {userRank.rank}위
          </p>
        </div>
      )}
    </div>
  );
};

export default RankingPage;
